import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import { Navbar, PageComponentProps, PageContainer, PageProviders } from '../../components';
import { MeasuresDrawerLoadingSkeleton } from '../../components/measures/MeasuresDrawer';
import { PendingMeasuresForm } from '../../components/measures/PendingMeasuresForm';
import { MeasureSchemaAndAnswers, PendingMeasuresFormLayout } from '../../components/measures/types';
import { Theme } from '../../state';
import { buildMeasurePages, filterMeasuresByType } from '../measures/helpers';
import { staleSoftBlockMeasureTypesAtom, visitorMeasuresAtom } from '../state';
import { PreSessionMeasuresActions } from './PreSessionMeasuresActions';

export function PreSessionMeasures({ onComplete, onSkip }: { onComplete: () => void; onSkip: () => void }) {
  const t = useTranslate();
  const measures = useAtomValue(visitorMeasuresAtom);
  const staleMeasureTypes = useAtomValue(staleSoftBlockMeasureTypesAtom);
  const pages = buildMeasurePages(filterMeasuresByType(measures, staleMeasureTypes));

  const actionsComponent = useCallback(
    (props: PageComponentProps<MeasureSchemaAndAnswers>) => (
      <PreSessionMeasuresActions {...props} onAllMeasuresCompleted={onComplete} />
    ),
    [onComplete],
  );

  return (
    <PageProviders theme={Theme.LIGHT}>
      <PageContainer>
        <Navbar showLanguageSwitcher />

        <PendingMeasuresForm
          layout={PendingMeasuresFormLayout.PAGE}
          pages={pages}
          actionsComponent={actionsComponent}
          loadingFallback={<MeasuresDrawerLoadingSkeleton />}
          emptyFallback={
            <div className='flex min-h-0 w-full flex-1 items-center justify-center'>
              <Button use='primary' onClick={onSkip}>
                {t('telehealth-core:preSessionMeasures.continueToWaitingRoom')}
              </Button>
            </div>
          }
          footerContent={
            <Button use='secondary' size='sm' onClick={onSkip}>
              {t('telehealth-core:preSessionMeasures.skip')}
            </Button>
          }
        />
      </PageContainer>
    </PageProviders>
  );
}
